import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Calendar, Loader2, ChevronDown, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { useAuth } from '@/components/AuthProvider';
import { getAuditLogs } from '@/integrations/supabase/permissions';
import { formatDate, formatTime } from '@/utils/dateUtils';
import { useEffect } from 'react';

interface ExpandedLog {
  [key: string]: boolean;
}

export function RegistroAtividades() {
  const navigate = useNavigate();
  const { empresa } = useAuth();
  const empresaId = empresa?.id;
  const [expandedLogs, setExpandedLogs] = useState<ExpandedLog>({});
  const [filterModule, setFilterModule] = useState('todos');

  const { data: auditLogs = [], isLoading } = useQuery({
    queryKey: ['audit-logs', empresaId, filterModule],
    queryFn: () => {
      if (!empresaId) return Promise.resolve([]);

      return getAuditLogs(empresaId, 200).then((logs) => {
        if (filterModule === 'todos') return logs;
        return logs.filter((log) => log.module === filterModule);
      });
    },
    enabled: !!empresaId
  });

  const toggleExpanded = (logId: string) => {
    setExpandedLogs((prev) => ({
      ...prev,
      [logId]: !prev[logId]
    }));
  };

  const getActionBadgeColor = (action: string) => {
    switch (action) {
      case 'create':
      case 'insert':
        return 'bg-green-100 text-green-800';
      case 'update':
        return 'bg-blue-100 text-blue-800';
      case 'delete':
        return 'bg-red-100 text-red-800';
      case 'login':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getActionLabel = (action: string) => {
    const labels: Record<string, string> = {
      create: 'Criado',
      insert: 'Inserido',
      update: 'Atualizado',
      delete: 'Deletado',
      login: 'Login',
      logout: 'Logout'
    };
    return labels[action] || action;
  };

  return (
    <div className="px-6 py-6 space-y-6">
      <div className="space-y-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/configuracoes')}
          className="mb-2 text-purple-600 hover:bg-purple-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar
        </Button>
        <h1 className="text-3xl font-bold">Registro de Atividades</h1>
        <p className="text-gray-500 mt-1">Acompanhe todas as ações realizadas no sistema</p>
      </div>

      <div className="flex gap-4">
        <Select value={filterModule} onValueChange={setFilterModule}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder="Filtrar por módulo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os módulos</SelectItem>
            <SelectItem value="clientes">Clientes</SelectItem>
            <SelectItem value="orcamentos">Orçamentos</SelectItem>
            <SelectItem value="pedidos">Pedidos</SelectItem>
            <SelectItem value="financeiro">Financeiro</SelectItem>
            <SelectItem value="estoque">Estoque</SelectItem>
            <SelectItem value="auth">Autenticação</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : auditLogs.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">Nenhuma atividade registrada</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {auditLogs.map((log) => (
            <Card key={log.id} className="hover:bg-gray-50 transition-colors">
              <div className="p-4">
                <div className="flex items-start justify-between cursor-pointer" onClick={() => toggleExpanded(log.id)}>
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <Badge className={getActionBadgeColor(log.action)}>
                        {getActionLabel(log.action)}
                      </Badge>
                      <span className="font-semibold">{log.module}</span>
                      {log.entity_type && (
                        <span className="text-sm text-gray-500">({log.entity_type})</span>
                      )}
                    </div>

                    <div className="flex gap-4 mt-2 text-sm text-gray-500">
                      <span>📅 {formatDate(log.created_at)}</span>
                      <span>⏰ {formatTime(log.created_at)}</span>
                    </div>
                  </div>

                  <ChevronDown
                    className={`w-5 h-5 text-gray-400 transition-transform ${expandedLogs[log.id] ? 'rotate-180' : ''}`}
                  />
                </div>

                {expandedLogs[log.id] && (
                  <div className="mt-4 pt-4 border-t space-y-3">
                    {log.changes && (
                      <div>
                        <h4 className="font-semibold text-sm mb-2">Alterações:</h4>
                        <pre className="bg-gray-100 p-3 rounded text-xs overflow-auto max-h-48">
                          {JSON.stringify(log.changes, null, 2)}
                        </pre>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4 text-sm">
                      {log.ip_address && (
                        <div>
                          <p className="text-gray-500">IP</p>
                          <p className="font-mono">{log.ip_address}</p>
                        </div>
                      )}
                      {log.user_agent && (
                        <div>
                          <p className="text-gray-500">Navegador</p>
                          <p className="text-xs truncate">{log.user_agent}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
